import React, { useEffect, useState } from "react";
import RecoveryNumberView from "../components/recovery-number-view";
import { stopEvent, getAPIError } from "lib/utility-functions";
import {
  recoveryPhoneNumberGet,
  recoveryPhoneNumberSendCode,
  recoveryPhoneNumberPatch,
  recoveryPhoneNumberVerifyCode,
  recoveryPhoneNumberResendCode,
  recoveryPhoneNumberDelete,
  isLoading,
  getPhoneNumber,
  getMobileNumberIsLoading
} from "modules/recovery-phone-number-module";
import { meLocale, meData } from "modules/me-module";
import { useDispatch, useSelector } from "react-redux";
import auth from "lib/profile-auth";
import { emailVerificationRequestPost } from "modules/identity-module";
import { uiShowToastrMessage } from "modules/ui-module";
import { setHandledErrorCodes, removeHandledErrorCodes } from 'lib/error-message-handling';
import { identityEmailToken } from "modules/identity-module";

const getErrorCodesArray = () => {
  return [
    400,
    404,
    429,
    'invalid.code',
    'verification.not.found',
  ];
};

export const RecoveryNumberContainer = () => {
  const [editPhoneNumberClick, setEditPhoneNumberClick] = useState(false);
  const [isPhoneNumberSetupAlready, setIsPhoneNumberSetupAlready] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const {
    meLocale,
    meEmail,
    isloading,
    recoveryPhoneNumber,
    getMobileNumberIsLoading,
    emailToken
  } = useSelector(getValuesFromStore);
  const [modalState, setModalState] = useState("");
  const [id,setId] = useState("");
  const [timer, setTimer] = useState(0);
  const [otp,setOtp] = useState("");
  const [disableVerifyCode,setDisableVerifyCode] = useState(true);
  const [disableSendCode,setDisableSendCode] = useState(true);
  const [isOpenVerificationModal, setIsOpenVerificationModal] = useState(false);
  const [error, setError] = useState("");
  const dispatch = useDispatch();
  const timerIntervalRef = React.useRef(null);
  const [maskedNumber ,setMaskedNumber] = useState("");

  const handleMaskedNumber = (number) => {
    const phoneNumber = String(number || '');
    const digits = phoneNumber.replace(/\D/g, '');
    const maskedDigits = '*'.repeat(Math.max(0, digits.length - 3));
    const lastThreeDigits = digits.slice(-3);
    setMaskedNumber(maskedDigits + lastThreeDigits);
  };

  const handleEditPhoneNumberClick = () => {
    setEditPhoneNumberClick(true);
    const minLoa = auth.token?.loa || 2;
    if (minLoa < 3) {
      setModalState("EmailVerification");
      setIsOpenVerificationModal(true);
      sendVerificationEmail();
    }
    else {
      setModalState("AddRecoveryPhoneNumber");
    }
  };

  const onChangePhoneNumber = (value) => {
    setError("");
    const formatted = value ? `+${value}` : "";
    setPhoneNumber(formatted);
    if(formatted.length <= 0 || formatted === recoveryPhoneNumber) {
      setDisableSendCode(true);
    }
    else {
      setDisableSendCode(false);
    }
  };

  const closeModal = () => {
    setEditPhoneNumberClick(false);
    setModalState("");
    setError("");
    const number = recoveryPhoneNumber || "";
    setPhoneNumber(number);
    handleMaskedNumber(number);
  };

  const handleResendCode = (e) => {
    stopEvent(e);
    setCountDown(30);
    setHandledErrorCodes(getErrorCodesArray());
    dispatch(recoveryPhoneNumberResendCode(id)).then(
      ()=>{},
      (error) => {
        const errorObj = getAPIError(error);
        const errorCode = errorObj && errorObj.length === 1 ? errorObj[0].code : '';
        if(errorCode === "verification.not.found") {
          displayToastrMessage(
            "error",
            "recovery.phone-number.verification.id.expired"
          );
          closeModal();
        }
        if(errorCode === "rate.limit.exceeded") {
          displayToastrMessage(
            "error",
            "recovery.phone-number.resend-code.rate-limit-exceeded"
          );
          closeModal();
        }
      }
    );
  };

  const onDeletePhoneNumber = (e) => {
    const authToken = `Email ${emailToken}`;
    setHandledErrorCodes(getErrorCodesArray());
    dispatch(recoveryPhoneNumberDelete(authToken)).then(
      (response) => {
        setPhoneNumber("");
        handleMaskedNumber("");
        setIsPhoneNumberSetupAlready(false);
        setModalState("PhoneNumberDeleted");
      },
    );
  };

  const setCountDown = (seconds) => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    setTimer(seconds);
    timerIntervalRef.current = setInterval(() => {
      setTimer((prevTimer) => {
      if (prevTimer <= 1) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
        return 0;
      }
      return prevTimer - 1;
      });
    }, 1000);
  };

  const handleModalCTAClick = (e, buttonId) => {
    stopEvent(e);
    const cta = e.target.id;

    switch (buttonId || cta) {
      case "send-verification-code":
        const payload ={
          mobileNumber: phoneNumber,
          locale: meLocale,
        };
        setHandledErrorCodes(getErrorCodesArray());
        dispatch(recoveryPhoneNumberSendCode(payload)).then(
          (response) => {
            setId(response?.payload?.id);
            setModalState("VerifyRecoveryPhoneNumber");
            setCountDown(30);
          },
          (error) => {
            const errorObj = getAPIError(error);
            const errorCode = errorObj && errorObj.length === 1 ? errorObj[0].code : '';
            if(errorCode === "invalid.mobileNumber") {
              setError("invalid-phone-number");
            }
            else if(errorCode === 'rate.limit.exceeded') {
              displayToastrMessage(
                "error",
                "recovery.phone-number.send-code.rate-limit-exceeded"
              );
              closeModal();
            }
          }
        );
        break;
      case "back":
        setModalState("AddRecoveryPhoneNumber");
        break;
      case "verify-code":
        setHandledErrorCodes(getErrorCodesArray());
        dispatch(recoveryPhoneNumberVerifyCode(id, { code: otp }))
          .then(
            (response) => {
              setPhoneNumber(response?.payload?.mobileNumber);
              const minLoa = auth.token?.loa || 2;
              if(response?.payload?.status === "VERIFIED") {
                const authToken = `Mobile ${id}${minLoa < 3 ? `, Email ${emailToken}` : ''}`;
                dispatch(recoveryPhoneNumberPatch(authToken, { mobileNumber: phoneNumber }))
                  .then((response) => {
                    setModalState("PhoneNumberVerified");
                    setIsPhoneNumberSetupAlready(true);
                  },
                  (error) => {
                    const errorObj = getAPIError(error);
                    const errorCode = errorObj && errorObj.length === 1 ? errorObj[0].code : '';
                    if(errorCode === 'mobile.not.verified') {
                       displayToastrMessage(
                        "error",
                        "recovery.phone-number.verification.id.expired"
                      );
                    }
                  });
              };
            },
            (error) => {
              removeHandledErrorCodes(getErrorCodesArray());
              const errorObj = getAPIError(error);
              const errorCode = errorObj && errorObj.length === 1 ? errorObj[0].code : '';
              if( errorCode === "invalid.code") {
                setError("invalid-code");
              }
              if(errorCode === "verification.not.found") {
                displayToastrMessage(
                  "error",
                  "recovery.phone-number.verification.id.expired"
                );
                closeModal();
              }
              if(errorCode === "rate.limit.exceeded") {
                displayToastrMessage(
                  "error",
                  "recovery.phone-number.verify.rate-limit-exceeded"
                );
                closeModal();
              }
            }
          );
        break;
      case "cancel":
        closeModal();
        break;
      case "got-it":
        closeModal();
        break;
      case "delete-phone-number":
        setModalState("DeleteRecoveryPhoneNumber");
        break;
      case "delete-back":
        setModalState("AddRecoveryPhoneNumber");
        break;
      case "delete-confirm":
        onDeletePhoneNumber(e);
        break;
    }
  };

  const onBlur = (value) => {
    setError("");
    if(value.length === 6) {
      setDisableVerifyCode(false);
    }
  };

  const onComplete = (value) => {
    setOtp(value);
    setDisableVerifyCode(false);
  };

  const displayToastrMessage = (toastrType, toastrMessageId, toastrParams = null) => {
    dispatch(uiShowToastrMessage(toastrType, toastrMessageId, toastrParams));
  };

  const sendVerificationEmail = () => {
    dispatch(
      emailVerificationRequestPost(meLocale, {
        email: meEmail,
      })
    ).then(
      () => {
        displayToastrMessage(
          "success",
          "email-editor.verification-email-send-successful"
        );
      },
      () => {
        displayToastrMessage(
          "error",
          "email-editor.verification-email-send-failed"
        );
      }
    );
  };

  const closeEmailVerificationModal = () => {
    setIsOpenVerificationModal(false);
    setModalState("");
  };

  const completeProcess = ()=> {
    setIsOpenVerificationModal(false);
    setEditPhoneNumberClick(true);
    setModalState("AddRecoveryPhoneNumber");
  };

  useEffect(() => {
    dispatch(recoveryPhoneNumberGet()).then((response) => {
      const number = response?.payload?.mobileNumber || "";
      setPhoneNumber(number);
      handleMaskedNumber(number);
      setIsPhoneNumberSetupAlready(!!response?.payload?.mobileNumber);
    });

    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

  }, [dispatch]);

  return (
    <RecoveryNumberView
      isPhoneNumberSetupAlready={isPhoneNumberSetupAlready}
      isLoading={isloading}
      getMobileNumberIsLoading={getMobileNumberIsLoading}
      onClickSetPhoneNumber={()=> setModalState("AddRecoveryPhoneNumber")}
      editPhoneNumberClick={editPhoneNumberClick}
      maskedNumber={maskedNumber}
      phoneNumber={phoneNumber}
      closeModal={closeModal}
      onChangePhoneNumber={onChangePhoneNumber}
      modalState={modalState}
      onSave={handleModalCTAClick}
      timer={timer}
      onClickResendCode={handleResendCode}
      onBlur={onBlur}
      onComplete={onComplete}
      disableVerifyCode={disableVerifyCode}
      onClickEditPhoneNumber={handleEditPhoneNumberClick}
      disableSendCode={disableSendCode}
      closeEmailVerificationModal={closeEmailVerificationModal}
      completeProcess={completeProcess}
      sendVerificationEmail={sendVerificationEmail}
      isOpenVerificationModal={isOpenVerificationModal}
      error={error}
    />
  );
};

export const getValuesFromStore = (state) => {
  const mePersonData = meData(state);
  const emails = mePersonData.emails || [];
  const primaryEmail = emails.find((email) => email.type === "primary") || "";
  return {
    meLocale: meLocale(state),
    meEmail: primaryEmail.value,
    isloading: isLoading(state),
    recoveryPhoneNumber: getPhoneNumber(state),
    getMobileNumberIsLoading: getMobileNumberIsLoading(state),
    emailToken: identityEmailToken(state),
  };
};

export default RecoveryNumberContainer;
